import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  Check,
  ChevronDown,
  ImageIcon,
  Layers,
  Languages,
  ListChecks,
  Loader2,
  Palette,
  Save,
  Search,
  Settings2,
  Upload,
  UtensilsCrossed,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  menuTemplatesApi,
  menusApi,
} from '../../api';
import axiosClient from '../../api/axiosClient';
import PublicMenuExperience from '../../components/menu/PublicMenuExperience';
import QrPrintStaticRecommendationsModal from '../../components/qrPrint/ai/QrPrintStaticRecommendationsModal';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { qrPrintApi } from '../../api/qrPrintApi';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import resolveMediaUrl from '../../utils/resolveMediaUrl';
import translateApiError from '../../utils/translateApiError';
import '../../styles/Menus/menu-form.css';
const DEFAULT_ESTABLISHMENT_ID = 1;
const SUPPORTED_LOCALES = ['fr', 'ar', 'en', 'es', 'de', 'zh', 'ru', 'nl', 'ja', 'it', 'pt', 'tr'];

// Older menus without language settings remain available in all 12 languages.
const readEnabledLanguages = (menu) => {
  const raw = menu?.enabled_languages ?? menu?.selected_languages ??
    menu?.available_languages ?? menu?.settings?.enabled_languages ??
    menu?.settings?.selected_languages ?? menu?.settings?.available_languages;
  if (raw === undefined || raw === null || raw === '') return [...SUPPORTED_LOCALES];

  let languages = raw;
  if (typeof languages === 'string') {
    try { languages = JSON.parse(languages); }
    catch { languages = languages.split(',').map((locale) => locale.trim()); }
  }
  if (languages && !Array.isArray(languages) && typeof languages === 'object') {
    languages = Object.keys(languages).filter((locale) => Boolean(languages[locale]));
  }
  if (!Array.isArray(languages)) return [...SUPPORTED_LOCALES];

  const selected = new Set(languages);
  const defaultLanguage = SUPPORTED_LOCALES.includes(menu?.default_language)
    ? menu.default_language : 'en';
  selected.add(defaultLanguage);
  return SUPPORTED_LOCALES.filter((locale) => selected.has(locale));
};
const PREVIEW_PHONE_WIDTH = 390;
const PREVIEW_PHONE_HEIGHT = 760;
const DEFAULT_FORM = {
  name: '',
  slug: '',
  description: '',
  default_language: 'en',
  enabled_languages: [...SUPPORTED_LOCALES],
  menu_template_id: '',
  sort_order: 0,
  is_active: false,
  is_default: false,
  image: null,
};
const DEFAULT_DESIGN_SETTINGS = {
  primary_color: '#51766E',
  background_color: '#F8FAF9',
  text_color: '#333333',
  show_images: true,
  show_search: true,
  show_category_tabs: true,
  show_category_sections: true,
  show_subcategories: true,
  show_featured_section: true,
  show_product_description: true,
  show_engagement: true,
  product_density: 'comfortable',
  border_radius: 'large',
};
const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (value && typeof value === 'object') {
    return Object.values(value);
  }
  return [];
};
const getMenuImageUrl = (menu) => {
  return resolveMediaUrl(
    menu?.image_url ||
      menu?.image_path ||
      menu?.image ||
      '',
  );
};
const getEstablishmentLogoUrl = (establishment) => {
  return resolveMediaUrl(
    establishment?.logo_url ||
      establishment?.logo ||
      establishment?.logo_path ||
      establishment?.media?.logo ||
      '',
  );
};
const normalizeTranslations = (translations) => {
  if (Array.isArray(translations)) {
    return translations.filter(Boolean);
  }
  if (translations && typeof translations === 'object') {
    return Object.entries(translations).map(
      ([locale, translation]) => {
        if (!translation || typeof translation !== 'object') {
          return {
            locale,
            name: translation ? String(translation) : '',
          };
        }
        return {
          locale: translation.locale || locale,
          ...translation,
        };
      },
    );
  }
  return [];
};
const getTranslationValue = (
  item,
  field,
  language,
  fallback = 'fr',
) => {
  if (!item) return '';
  if (item?.translations?.[language]?.[field]) {
    return item.translations[language][field];
  }
  if (item?.translations?.[fallback]?.[field]) {
    return item.translations[fallback][field];
  }
  const translations = normalizeTranslations(
    item.translations,
  );
  const selectedTranslation =
    translations.find(
      (translation) => translation.locale === language,
    ) ||
    translations.find(
      (translation) => translation.locale === fallback,
    ) ||
    translations.find(
      (translation) => translation.locale === 'en',
    ) ||
    translations.find(
      (translation) => translation.locale === 'ar',
    ) ||
    translations.find(
      (translation) => translation.locale === 'es',
    ) ||
    translations[0];
  return (
    selectedTranslation?.[field] ||
    item?.[field] ||
    ''
  );
};
const flattenCategories = (categories = []) => {
  return toArray(categories).flatMap((category) => [
    category,
    ...flattenCategories(category.children),
  ]);
};
const getItemIdList = (items = []) => {
  return toArray(items)
    .map((item) => Number(item?.id || item))
    .filter((id) => Number.isInteger(id) && id > 0);
};
const updateEstablishmentLogo = (establishmentId, logoFile) => {
  const formData = new FormData();
  formData.append('_method', 'PATCH');
  formData.append('logo', logoFile);
  return axiosClient.post(
    `/establishments/${establishmentId}/menu-settings`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};
const getTemplateName = (template, language = 'fr') => {
  return (
    getTranslationValue(template, 'name', language) ||
    template?.name ||
    ''
  );
};

function LanguageDropdown({ value, onChange, options, label, invalid = false, icon: DropdownIcon = Languages }) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const currentIndex = Math.max(0, options.findIndex((option) => option.value === value));
  const [highlightedIndex, setHighlightedIndex] = useState(currentIndex);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const choose = (locale) => {
    onChange(locale);
    setOpen(false);
    rootRef.current?.querySelector('button[aria-haspopup="listbox"]')?.focus();
  };

  const onTriggerKeyDown = (event) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(event.key)) return;
    event.preventDefault();
    if (!open) {
      setHighlightedIndex(currentIndex);
      setOpen(true);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') return choose(options[highlightedIndex].value);
    if (event.key === 'Home') return setHighlightedIndex(0);
    if (event.key === 'End') return setHighlightedIndex(options.length - 1);
    setHighlightedIndex((index) =>
      (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length,
    );
  };

  return (
    <div ref={rootRef} className="menu-form-language-dropdown" style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={invalid}
        onKeyDown={onTriggerKeyDown}
        onClick={() => {
          setHighlightedIndex(currentIndex);
          setOpen((previous) => !previous);
        }}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, padding: '12px 14px', minHeight: 46, borderRadius: 12,
          border: `1px solid ${invalid ? '#c24141' : open ? '#51766E' : '#d5ddda'}`,
          background: 'var(--surface, #fff)', color: 'inherit', cursor: 'pointer',
          textAlign: 'start', font: 'inherit', boxShadow: open ? '0 0 0 3px rgba(81,118,110,.13)' : 'none',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <DropdownIcon size={17} aria-hidden="true" />
          {options[currentIndex]?.label || value}
        </span>
        <ChevronDown size={17} aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
      </button>
      {open && (
        <div
          role="listbox"
          aria-label={label}
          style={{
            position: 'absolute', zIndex: 50, top: 'calc(100% + 6px)', insetInline: 0,
            maxHeight: 280, overflowY: 'auto', padding: 5, borderRadius: 12,
            border: '1px solid #d5ddda', background: 'var(--surface, #fff)',
            boxShadow: '0 12px 30px rgba(0,0,0,.16)',
          }}
        >
          {options.map((option, index) => (
            <button
              type="button"
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => choose(option.value)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 10, width: '100%', padding: '10px 12px', border: 0,
                borderRadius: 8, textAlign: 'start', cursor: 'pointer', font: 'inherit',
                color: 'inherit', background: highlightedIndex === index ? 'rgba(81,118,110,.13)' : 'transparent',
                fontWeight: option.value === value ? 650 : 400,
              }}
            >
              <span>{option.label}</span>
              {option.value === value && <Check size={16} aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MenuForm() {
  const { menuId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { showAlert } = useAlerts();
  const { getFeature } = useAuth();
  const qrAiFeature = getFeature(
    'qr_print_ai_design',
  );
  const qrAiRecommendationsAvailable =
    qrAiFeature?.visible === true &&
    qrAiFeature?.available === true &&
    qrAiFeature?.locked !== true;
  const isEditMode = Boolean(menuId);
  const imageInputRef = useRef(null);
  const restaurantLogoInputRef = useRef(null);
  const previewStageRef = useRef(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [templates, setTemplates] = useState([]);
  const [menuPreviewData, setMenuPreviewData] =
    useState(null);
  const [establishmentData, setEstablishmentData] =
    useState(null);
  const [availableCategories, setAvailableCategories] =
    useState([]);
  const [availableProducts, setAvailableProducts] =
    useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] =
    useState([]);
  const [selectedProductIds, setSelectedProductIds] =
    useState([]);
  const [featuredProductIds, setFeaturedProductIds] =
    useState([]);
  const [categorySearchTerm, setCategorySearchTerm] =
    useState('');
  const [productSearchTerm, setProductSearchTerm] =
    useState('');
  const [imagePreview, setImagePreview] =
    useState('');
  const [restaurantLogoFile, setRestaurantLogoFile] =
    useState(null);
  const [restaurantLogoPreview, setRestaurantLogoPreview] =
    useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(
    isEditMode,
  );
  const [saving, setSaving] = useState(false);
  const [loadingPreview, setLoadingPreview] =
    useState(false);
  const [previewZoom, setPreviewZoom] =
    useState(0.7);
  const [isPreviewZoomManual, setIsPreviewZoomManual] =
    useState(false);
  const [
    qrStaticRecommendationsOpen,
    setQrStaticRecommendationsOpen,
  ] = useState(false);
  const [createdMenu, setCreatedMenu] =
    useState(null);
  const establishmentId =
    localStorage.getItem(
      'selected_establishment_id',
    ) || DEFAULT_ESTABLISHMENT_ID;
  const selectedTemplate = useMemo(() => {
    return templates.find(
      (template) =>
        Number(template.id) ===
        Number(form.menu_template_id),
    );
  }, [templates, form.menu_template_id]);
  const previewDesignSettings = useMemo(
    () => ({
      ...DEFAULT_DESIGN_SETTINGS,
      ...(selectedTemplate?.config || {}),
      ...(menuPreviewData?.menu?.design_settings || {}),
      ...(menuPreviewData?.menu?.menu_design_settings || {}),
    }),
    [
      selectedTemplate,
      menuPreviewData?.menu?.design_settings,
      menuPreviewData?.menu?.menu_design_settings,
    ],
  );
  const previewMenu = useMemo(
    () => ({
      ...(menuPreviewData?.menu || {}),
      ...(isEditMode
        ? {}
        : {
            id: null,
          }),
      name:
        form.name ||
        menuPreviewData?.menu?.name ||
        t('menus.form.previewUntitled'),
      description:
        form.description ||
        menuPreviewData?.menu?.description ||
        '',
      default_language:
        form.default_language ||
        menuPreviewData?.menu
          ?.default_language ||
        'en',
      enabled_languages: SUPPORTED_LOCALES.filter((locale) =>
        form.enabled_languages.includes(locale) || locale === form.default_language
      ),
      is_active: Boolean(form.is_active),
      is_default: Boolean(form.is_default),
      design_settings:
        previewDesignSettings,
    }),
    [
      menuPreviewData?.menu,
      form.name,
      form.description,
      form.default_language,
      form.enabled_languages,
      form.is_active,
      form.is_default,
      previewDesignSettings,
      isEditMode,
      t,
    ],
  );
  const previewEstablishment = {
    ...(establishmentData ||
      menuPreviewData?.establishment || {
        id: establishmentId,
        name:
          localStorage.getItem(
            'selected_establishment_name',
          ) || t('menuDesign.restaurantName'),
      }),
    ...(restaurantLogoPreview
      ? {
          logo: restaurantLogoPreview,
          logo_url: restaurantLogoPreview,
        }
      : {}),
  };
  const flattenedAvailableCategories = useMemo(
    () => flattenCategories(availableCategories),
    [availableCategories],
  );
  const selectedCategoryIdSet = useMemo(
    () =>
      new Set(
        selectedCategoryIds.map((id) => String(id)),
      ),
    [selectedCategoryIds],
  );
  const selectedProductIdSet = useMemo(
    () =>
      new Set(
        selectedProductIds.map((id) => String(id)),
      ),
    [selectedProductIds],
  );
  const featuredProductIdSet = useMemo(
    () =>
      new Set(
        featuredProductIds.map((id) => String(id)),
      ),
    [featuredProductIds],
  );
  const filteredAvailableCategories = useMemo(() => {
    const query = categorySearchTerm
      .trim()
      .toLowerCase();
    return flattenedAvailableCategories.filter(
      (category) => {
        const name = getTranslationValue(
          category,
          'name',
          language,
          form.default_language,
        );
        const description = getTranslationValue(
          category,
          'description',
          language,
          form.default_language,
        );
        return (
          !query ||
          name.toLowerCase().includes(query) ||
          description.toLowerCase().includes(query)
        );
      },
    );
  }, [
    flattenedAvailableCategories,
    categorySearchTerm,
    language,
    form.default_language,
  ]);
  const filteredAvailableProducts = useMemo(() => {
    const query = productSearchTerm
      .trim()
      .toLowerCase();
    return availableProducts.filter((product) => {
      const name = getTranslationValue(
        product,
        'name',
        language,
        form.default_language,
      );
      const description = getTranslationValue(
        product,
        'description',
        language,
        form.default_language,
      );
      const category = flattenedAvailableCategories.find(
        (currentCategory) =>
          Number(currentCategory.id) ===
          Number(product.category_id),
      );
      const categoryName = getTranslationValue(
        category,
        'name',
        language,
        form.default_language,
      );
      return (
        !query ||
        name.toLowerCase().includes(query) ||
        description.toLowerCase().includes(query) ||
        categoryName.toLowerCase().includes(query)
      );
    });
  }, [
    availableProducts,
    flattenedAvailableCategories,
    productSearchTerm,
    language,
    form.default_language,
  ]);
  const previewCategories = useMemo(() => {
    if (availableCategories.length === 0) {
      return toArray(menuPreviewData?.categories);
    }
    const filterCategoryTree = (categories = []) => {
      return toArray(categories)
        .map((category) => {
          const children = filterCategoryTree(
            category.children,
          );
          const isSelected =
            selectedCategoryIdSet.has(
              String(category.id),
            ) || children.length > 0;
          if (!isSelected) {
            return null;
          }
          return {
            ...category,
            children,
          };
        })
        .filter(Boolean);
    };
    return filterCategoryTree(availableCategories);
  }, [
    availableCategories,
    menuPreviewData?.categories,
    selectedCategoryIdSet,
  ]);
  const previewProducts = useMemo(() => {
    if (availableProducts.length === 0) {
      return toArray(menuPreviewData?.products);
    }
    return availableProducts.filter((product) =>
      selectedProductIdSet.has(String(product.id)),
    );
  }, [
    availableProducts,
    menuPreviewData?.products,
    selectedProductIdSet,
  ]);
  const previewFeaturedProducts = useMemo(() => {
    return previewProducts.filter((product) =>
      featuredProductIdSet.has(String(product.id)),
    );
  }, [
    previewProducts,
    featuredProductIdSet,
  ]);
  const featuredProductsCount =
    featuredProductIds.length;
  const selectedCategoriesCount =
    selectedCategoryIds.length;
  const selectedProductsCount =
    selectedProductIds.length;
  const effectivePreviewTemplate =
    selectedTemplate ||
    menuPreviewData?.template ||
    null;
  const handleFieldChange = (
    field,
    value,
  ) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
    setErrors((current) => {
      if (!current[field]) {
        return current;
      }
      const nextErrors = {
        ...current,
      };
      delete nextErrors[field];
      return nextErrors;
    });
  };
  const handleDefaultLanguageChange = (locale) => {
    setForm((current) => ({
      ...current,
      default_language: locale,
      enabled_languages: SUPPORTED_LOCALES.filter((value) =>
        current.enabled_languages.includes(value) || value === locale
      ),
    }));
    setErrors((current) => ({ ...current, default_language: undefined, enabled_languages: undefined }));
  };

  const toggleMenuLanguage = (locale) => {
    setForm((current) => {
      if (locale === current.default_language) return current;
      const next = new Set(current.enabled_languages);
      if (next.has(locale)) next.delete(locale);
      else next.add(locale);
      next.add(current.default_language);
      return {
        ...current,
        enabled_languages: SUPPORTED_LOCALES.filter((value) => next.has(value)),
      };
    });
    setErrors((current) => ({ ...current, enabled_languages: undefined }));
  };

  const handleRestaurantLogoChange = (event) => {
    const file =
      event.target.files?.[0] || null;
    if (!file) return;
    setRestaurantLogoFile(file);
    if (
      restaurantLogoPreview?.startsWith('blob:')
    ) {
      URL.revokeObjectURL(
        restaurantLogoPreview,
      );
    }
    setRestaurantLogoPreview(
      URL.createObjectURL(file),
    );
  };
  const handleCancelRestaurantLogo = () => {
    setRestaurantLogoFile(null);
    if (
      restaurantLogoPreview?.startsWith('blob:')
    ) {
      URL.revokeObjectURL(
        restaurantLogoPreview,
      );
    }
    setRestaurantLogoPreview(
      getEstablishmentLogoUrl(
        establishmentData ||
          menuPreviewData?.establishment,
      ) || '',
    );
    if (restaurantLogoInputRef.current) {
      restaurantLogoInputRef.current.value = '';
    }
  };
  const handleImageChange = (event) => {
    const file =
      event.target.files?.[0] || null;
    if (!file) return;
    handleFieldChange('image', file);
    setImagePreview(
      URL.createObjectURL(file),
    );
  };
  const handleRemoveImage = () => {
    handleFieldChange('image', null);
    if (imagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview('');
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };
  const toggleCategorySelection = (categoryId) => {
    const normalizedId = Number(categoryId);
    setSelectedCategoryIds((currentIds) => {
      const exists = currentIds.some(
        (id) => Number(id) === normalizedId,
      );
      if (exists) {
        return currentIds.filter(
          (id) => Number(id) !== normalizedId,
        );
      }
      return [...currentIds, normalizedId];
    });
  };
  const toggleProductSelection = (product) => {
    const normalizedId = Number(product?.id);
    if (!normalizedId) return;
    const isCurrentlySelected =
      selectedProductIds.some(
        (id) => Number(id) === normalizedId,
      );
    setSelectedProductIds((currentIds) => {
      const exists = currentIds.some(
        (id) => Number(id) === normalizedId,
      );
      if (exists) {
        return currentIds.filter(
          (id) => Number(id) !== normalizedId,
        );
      }
      return [...currentIds, normalizedId];
    });
    /*
     * A product cannot stay featured if it is removed
     * from this menu.
     */
    if (isCurrentlySelected) {
      setFeaturedProductIds((currentIds) =>
        currentIds.filter(
          (id) => Number(id) !== normalizedId,
        ),
      );
    }
    if (product?.category_id) {
      setSelectedCategoryIds((currentIds) => {
        const categoryId = Number(product.category_id);
        if (
          currentIds.some(
            (id) => Number(id) === categoryId,
          )
        ) {
          return currentIds;
        }
        return [...currentIds, categoryId];
      });
    }
  };
  const selectAllVisibleCategories = () => {
    setSelectedCategoryIds((currentIds) => {
      const mergedIds = new Set(
        currentIds.map((id) => Number(id)),
      );
      filteredAvailableCategories.forEach((category) => {
        mergedIds.add(Number(category.id));
      });
      return Array.from(mergedIds);
    });
  };
  const clearSelectedCategories = () => {
    setSelectedCategoryIds([]);
  };
  const selectAllVisibleProducts = () => {
    setSelectedProductIds((currentIds) => {
      const mergedIds = new Set(
        currentIds.map((id) => Number(id)),
      );
      filteredAvailableProducts.forEach((product) => {
        mergedIds.add(Number(product.id));
      });
      return Array.from(mergedIds);
    });
    setSelectedCategoryIds((currentIds) => {
      const mergedIds = new Set(
        currentIds.map((id) => Number(id)),
      );
      filteredAvailableProducts.forEach((product) => {
        if (product.category_id) {
          mergedIds.add(Number(product.category_id));
        }
      });
      return Array.from(mergedIds);
    });
  };
  const clearSelectedProducts = () => {
    setSelectedProductIds([]);
    setFeaturedProductIds([]);
  };
  const toggleFeaturedProduct = (productId) => {
    const normalizedId = Number(productId);
    if (!normalizedId) return;
    /*
     * Only products already included in this menu
     * can be promoted to the top featured section.
     */
    if (
      !selectedProductIds.some(
        (id) => Number(id) === normalizedId,
      )
    ) {
      return;
    }
    setFeaturedProductIds((currentIds) => {
      const exists = currentIds.some(
        (id) => Number(id) === normalizedId,
      );
      if (exists) {
        return currentIds.filter(
          (id) => Number(id) !== normalizedId,
        );
      }
      return [...currentIds, normalizedId];
    });
  };
  const clearFeaturedProducts = () => {
    setFeaturedProductIds([]);
  };
  const selectAllVisibleFeaturedProducts = () => {
    const visibleSelectedProductIds =
      filteredAvailableProducts
        .filter((product) =>
          selectedProductIdSet.has(
            String(product.id),
          ),
        )
        .map((product) => Number(product.id));
    setFeaturedProductIds((currentIds) => {
      const mergedIds = new Set(
        currentIds.map((id) => Number(id)),
      );
      visibleSelectedProductIds.forEach((id) => {
        mergedIds.add(id);
      });
      return Array.from(mergedIds);
    });
  };
  const loadTemplates = async () => {
    const response =
      await menuTemplatesApi.getTemplates({
        per_page: 100,
      });
    const templatesList =
      response.data?.data?.data ||
      response.data?.data ||
      [];
    setTemplates(
      Array.isArray(templatesList)
        ? templatesList
        : [],
    );
    return Array.isArray(templatesList)
      ? templatesList
      : [];
  };
  const loadEstablishmentIdentity = async () => {
    try {
      const response =
        await menuTemplatesApi.getMenuPreview(
          establishmentId,
        );
      const preview =
        response.data?.data || null;
      if (preview?.establishment) {
        setEstablishmentData(
          preview.establishment,
        );
        setRestaurantLogoPreview(
          getEstablishmentLogoUrl(
            preview.establishment,
          ) || '',
        );
      }
    } catch {
      setEstablishmentData(null);
    }
  };
  const loadReusableContent = async () => {
    const [categoriesResponse, productsResponse] =
      await Promise.all([
        axiosClient.get(
          `/establishments/${establishmentId}/categories`,
          {
            params: {
              is_active: 1,
              tree: 1,
              per_page: 100,
            },
          },
        ),
        axiosClient.get(
          `/establishments/${establishmentId}/products`,
          {
            params: {
              per_page: 100,
            },
          },
        ),
      ]);
    const categoriesList =
      categoriesResponse.data?.data?.data ||
      categoriesResponse.data?.data ||
      [];
    const productsList =
      productsResponse.data?.data?.data ||
      productsResponse.data?.data ||
      [];
    setAvailableCategories(toArray(categoriesList));
    setAvailableProducts(toArray(productsList));
  };
  const loadExistingMenu = async (
    templatesList = [],
  ) => {
    if (!isEditMode) return;
    const response =
      await menusApi.getMenu(menuId);
    const menu =
      response.data?.data || null;
    if (!menu) {
      throw new Error(t('menus.details.notFound'));
    }
    setForm({
      name: menu.name || '',
      slug: menu.slug || '',
      description:
        menu.description || '',
      default_language:
        menu.default_language || 'en',
      enabled_languages: readEnabledLanguages(menu),
      menu_template_id:
        menu.menu_template_id ||
        menu.menu_template?.id ||
        menu.template?.id ||
        templatesList[0]?.id ||
        '',
      sort_order:
        Number(menu.sort_order || 0),
      is_active:
        menu.is_active !== false,
      is_default:
        Boolean(menu.is_default),
      image: null,
    });
    setSelectedCategoryIds(
      getItemIdList(
        menu.category_ids ||
          menu.categories,
      ),
    );
    setSelectedProductIds(
      getItemIdList(
        menu.product_ids ||
          menu.products,
      ),
    );
    setFeaturedProductIds(
      getItemIdList(
        menu.featured_product_ids || [],
      ),
    );
    setImagePreview(
      getMenuImageUrl(menu),
    );
    localStorage.setItem(
      'selected_menu_id',
      String(menu.id),
    );
  };
  const loadPreview = async () => {
    if (!isEditMode) {
      setMenuPreviewData(null);
      return;
    }
    try {
      setLoadingPreview(true);
      const response =
        await menuTemplatesApi.getMenuPreview(
          establishmentId,
          menuId,
        );
      const preview =
        response.data?.data || null;
      setMenuPreviewData(preview);
      if (preview?.menu) {
        setSelectedCategoryIds((currentIds) =>
          currentIds.length > 0
            ? currentIds
            : getItemIdList(
                preview.menu.category_ids ||
                  preview.menu.categories,
              ),
        );
        setSelectedProductIds((currentIds) =>
          currentIds.length > 0
            ? currentIds
            : getItemIdList(
                preview.menu.product_ids ||
                  preview.menu.products,
              ),
        );
        /*
         * Do not update featuredProductIds from the preview.
         *
         * loadExistingMenu() is the only place that initializes the
         * saved top-product selection. This is important because an
         * empty array is a valid user choice. If we refill it here,
         * clearing all top products would immediately select them
         * again after the preview reloads.
         */
      }
      if (preview?.establishment) {
        setEstablishmentData(
          preview.establishment,
        );
        if (!restaurantLogoFile) {
          setRestaurantLogoPreview(
            getEstablishmentLogoUrl(
              preview.establishment,
            ) || '',
          );
        }
      }
    } catch (error) {
      setMenuPreviewData(null);
    } finally {
      setLoadingPreview(false);
    }
  };
  useEffect(() => {
    let ignore = false;
    const initialize = async () => {
      try {
        setLoading(true);
        const templatesList =
          await loadTemplates();
        await loadReusableContent();
        if (ignore) return;
        if (isEditMode) {
          await loadExistingMenu(
            templatesList,
          );
          if (ignore) return;
          await loadPreview();
        } else {
          if (templatesList[0]?.id) {
            setForm((current) => ({
              ...current,
              menu_template_id:
                templatesList[0].id,
            }));
          }
          await loadEstablishmentIdentity();
        }
      } catch (error) {
        if (ignore) return;
        showAlert({
          type: 'error',
          title: t('alerts.error'),
          message: translateApiError(
            error,
            t,
          ),
        });
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };
    initialize();
    return () => {
      ignore = true;
    };
    // Intentionally initialize only when the record changes.
  }, [menuId]);
  useEffect(() => {
    return () => {
      if (
        imagePreview?.startsWith('blob:')
      ) {
        URL.revokeObjectURL(
          imagePreview,
        );
      }
      if (
        restaurantLogoPreview?.startsWith('blob:')
      ) {
        URL.revokeObjectURL(
          restaurantLogoPreview,
        );
      }
    };
  }, [imagePreview, restaurantLogoPreview]);
  const fitPreviewToCanvas = useCallback(() => {
    const previewStage =
      previewStageRef.current;
    if (!previewStage) {
      return;
    }
    const { width, height } =
      previewStage.getBoundingClientRect();
    if (!width || !height) {
      return;
    }
    const availableWidth =
      Math.max(width - 56, 260);
    const availableHeight =
      Math.max(height - 64, 360);
    const nextZoom = Math.min(
      availableWidth /
        PREVIEW_PHONE_WIDTH,
      availableHeight /
        PREVIEW_PHONE_HEIGHT,
      1.12,
    );
    setPreviewZoom(
      Number(
        Math.max(
          0.28,
          nextZoom,
        ).toFixed(2),
      ),
    );
  }, []);
  const scrollPhonePreview =
    useCallback((event) => {
      const previewStage =
        previewStageRef.current;
      if (!previewStage) {
        return false;
      }
      const scrollRegion =
        previewStage.querySelector(
          '.menu-form-public-phone .public-menu--phone-preview > .public-menu__scroll-region',
        ) ||
        previewStage.querySelector(
          '.menu-form-public-phone .public-menu--phone-preview',
        );
      if (!scrollRegion) {
        return false;
      }
      const maxScrollTop =
        scrollRegion.scrollHeight -
        scrollRegion.clientHeight;
      if (maxScrollTop <= 0) {
        return false;
      }
      const nextScrollTop =
        Math.min(
          maxScrollTop,
          Math.max(
            0,
            scrollRegion.scrollTop +
              event.deltaY,
          ),
        );
      if (
        nextScrollTop ===
        scrollRegion.scrollTop
      ) {
        return false;
      }
      event.preventDefault();
      event.stopPropagation();
      scrollRegion.scrollTop =
        nextScrollTop;
      return true;
    }, []);
  useEffect(() => {
    const handleGlobalWheel = (
      event,
    ) => {
      if (
        event.ctrlKey ||
        event.metaKey
      ) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const previewStage =
          previewStageRef.current;
        if (
          !previewStage ||
          !previewStage.contains(
            event.target,
          )
        ) {
          return;
        }
        const direction =
          event.deltaY > 0 ? -1 : 1;
        setIsPreviewZoomManual(true);
        setPreviewZoom(
          (currentZoom) =>
            Number(
              Math.min(
                1.25,
                Math.max(
                  0.28,
                  currentZoom +
                    direction * 0.05,
                ),
              ).toFixed(2),
            ),
        );
        return;
      }
      const previewStage =
        previewStageRef.current;
      if (
        !previewStage ||
        !previewStage.contains(
          event.target,
        )
      ) {
        return;
      }
      const isInsidePhone =
        Boolean(
          event.target.closest(
            '.menu-form-public-phone',
          ),
        );
      if (isInsidePhone) {
        scrollPhonePreview(event);
      }
    };
    document.addEventListener(
      'wheel',
      handleGlobalWheel,
      {
        passive: false,
        capture: true,
      },
    );
    return () => {
      document.removeEventListener(
        'wheel',
        handleGlobalWheel,
        {
          capture: true,
        },
      );
    };
  }, [scrollPhonePreview]);
  useEffect(() => {
    const previewStage =
      previewStageRef.current;
    if (!previewStage) {
      return undefined;
    }
    if (!isPreviewZoomManual) {
      requestAnimationFrame(
        fitPreviewToCanvas,
      );
      window.setTimeout(
        fitPreviewToCanvas,
        80,
      );
    }
    const resizeObserver =
      new ResizeObserver(() => {
        if (!isPreviewZoomManual) {
          requestAnimationFrame(
            fitPreviewToCanvas,
          );
        }
      });
    resizeObserver.observe(
      previewStage,
    );
    return () => {
      resizeObserver.disconnect();
    };
  }, [
    fitPreviewToCanvas,
    isPreviewZoomManual,
    loadingPreview,
  ]);
  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) {
      nextErrors.name =
        t('menus.form.errors.nameRequired');
    }
    if (!form.default_language) {
      nextErrors.default_language =
        t(
          'menus.form.errors.languageRequired',
        );
    }
    if (
      form.sort_order === '' ||
      Number.isNaN(
        Number(form.sort_order),
      ) ||
      Number(form.sort_order) < 0
    ) {
      nextErrors.sort_order =
        t(
          'menus.form.errors.sortOrderInvalid',
        );
    }
    if (!form.enabled_languages.includes(form.default_language)) {
      nextErrors.enabled_languages = t('validation.messages.required');
    }
    setErrors(nextErrors);
    return (
      Object.keys(nextErrors).length === 0
    );
  };
  const buildPayload = () => {
    return {
      establishment_id:
        Number(establishmentId),
      name: form.name.trim(),
      description:
        form.description.trim() || '',
      default_language:
        form.default_language,
      enabled_languages: SUPPORTED_LOCALES.filter((locale) =>
        form.enabled_languages.includes(locale) || locale === form.default_language
      ),
      menu_template_id:
        form.menu_template_id
          ? Number(
              form.menu_template_id,
            )
          : '',
      sort_order: Number(
        form.sort_order || 0,
      ),
      is_active: Boolean(
        form.is_active,
      ),
      is_default: Boolean(
        form.is_default,
      ),
      category_ids: selectedCategoryIds,
      product_ids: selectedProductIds,
      featured_product_ids: featuredProductIds.filter(
        (id) =>
          selectedProductIds.some(
            (productId) =>
              Number(productId) === Number(id),
          ),
      ),
      ...(form.image
        ? {
            image: form.image,
          }
        : {}),
    };
  };
  const handleSubmit = async (
    event,
  ) => {
    event.preventDefault();
    if (!validate()) {
      const firstInvalidField =
        document.querySelector(
          '.menu-form-field--invalid input, .menu-form-field--invalid textarea, .menu-form-field--invalid select',
        );
      firstInvalidField?.focus();
      return;
    }
    try {
      setSaving(true);
      const payload = buildPayload();
      const response = isEditMode
        ? await menusApi.updateMenu(
            menuId,
            payload,
          )
        : await menusApi.createMenu(
            establishmentId,
            payload,
          );
      const savedMenu =
        response.data?.data || null;
      const savedMenuId =
        savedMenu?.id || menuId;
      // Reflect the exact slug saved by Laravel without refreshing the page.
      if (savedMenu?.slug) {
        setForm((current) => ({ ...current, slug: savedMenu.slug }));
      }
      if (savedMenuId) {
        localStorage.setItem(
          'selected_menu_id',
          String(savedMenuId),
        );
      }
      if (restaurantLogoFile) {
        const logoResponse =
          await updateEstablishmentLogo(
            establishmentId,
            restaurantLogoFile,
          );
        const updatedEstablishment =
          logoResponse.data?.data
            ?.establishment ||
          logoResponse.data?.data ||
          null;
        if (updatedEstablishment) {
          setEstablishmentData(
            (current) => ({
              ...(current || {}),
              ...updatedEstablishment,
            }),
          );
          setRestaurantLogoPreview(
            getEstablishmentLogoUrl(
              updatedEstablishment,
            ) || restaurantLogoPreview,
          );
        }
        setRestaurantLogoFile(null);
        if (restaurantLogoInputRef.current) {
          restaurantLogoInputRef.current.value = '';
        }
      }
      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: isEditMode
          ? t(
              'menus.form.alerts.updated',
            )
          : t(
              'menus.form.alerts.created',
            ),
      });
      if (!isEditMode && savedMenuId) {
        const createdMenuData = {
          ...(savedMenu || {}),
          id: savedMenuId,
          establishment_id:
            savedMenu?.establishment_id ||
            Number(establishmentId),
        };
        setCreatedMenu(createdMenuData);
        /*
         * The static Super Admin examples are immediate in BOTH cases.
         * AI generation is separate and never blocks this popup.
         */
        setQrStaticRecommendationsOpen(true);
        if (qrAiRecommendationsAvailable) {
          qrPrintApi
            .startRecommendationBatch(
              savedMenuId,
            )
            .catch(
              (requestError) => {
                console.error(
                  'Failed to start background QR AI recommendations:',
                  requestError,
                );
              },
            );
        }
        return;
      }
      if (isEditMode) {
        await loadPreview();
      }
    } catch (error) {
      const validationErrors =
        error.response?.data?.errors ||
        {};
      setErrors(
        Object.fromEntries(
          Object.entries(
            validationErrors,
          ).map(
            ([key, value]) => [
              key,
              Array.isArray(value)
                ? value[0]
                : value,
            ],
          ),
        ),
      );
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translateApiError(
          error,
          t,
        ),
      });
    } finally {
      setSaving(false);
    }
  };
  if (loading) {
    return (
      <div className="menu-form-page">
        <div className="menu-form-loading">
          <Loader2 size={34} />
          <h2>
            {t('common.loading')}
          </h2>
        </div>
      </div>
    );
  }
  return (
    <div className="menu-form-page">
      <section className="menu-form-heading">
        <div className="menu-form-heading__main">
          <button
            type="button"
            className="menu-form-back-button"
            onClick={() => navigate(-1)}
            aria-label={t('common.back')}
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <p className="menu-form-heading__eyebrow">
              {t(
                'menus.form.eyebrow',
              )}
            </p>
            <h1>
              {isEditMode
                ? t(
                    'menus.form.editTitle',
                  )
                : t(
                    'menus.form.createTitle',
                  )}
            </h1>
            <p>
              {isEditMode
                ? t(
                    'menus.form.editDescription',
                  )
                : t(
                    'menus.form.createDescription',
                  )}
            </p>
          </div>
        </div>
        <div className="menu-form-heading__actions">
          {isEditMode && (
            <Link
              to={`/menus/${menuId}/customize`}
              className="dashboard-secondary-button menu-form-customize-button"
            >
              <Palette size={18} />
              {t(
                'menus.form.customize',
              )}
            </Link>
          )}
          <button
            type="submit"
            form="menu-form"
            className="dashboard-primary-button"
            disabled={saving}
          >
            {saving ? (
              <Loader2 size={18} />
            ) : (
              <Save size={18} />
            )}
            {saving
              ? t('common.saving')
              : isEditMode
                ? t(
                    'common.saveChanges',
                  )
                : t('common.save')}
          </button>
        </div>
      </section>
      <section className="menu-form-layout">
        <form
          id="menu-form"
          className="menu-form-card"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="menu-form-section-heading">
            <span>
              <UtensilsCrossed
                size={19}
              />
            </span>
            <div>
              <h2>
                {t(
                  'menus.form.informationTitle',
                )}
              </h2>
              <p>
                {t(
                  'menus.form.informationDescription',
                )}
              </p>
            </div>
          </div>
          <div className="menu-form-fields menu-form-fields--compact">
            <label
              className={`menu-form-field ${
                errors.name
                  ? 'menu-form-field--invalid'
                  : ''
              }`}
            >
              <span>
                {t(
                  'menus.form.name',
                )}{' '}
                *
              </span>
              <input
                type="text"
                value={form.name}
                onChange={(event) =>
                  handleFieldChange(
                    'name',
                    event.target.value,
                  )
                }
                placeholder={t(
                  'menus.form.namePlaceholder',
                )}
                autoFocus={!isEditMode}
              />
              {errors.name && (
                <small>
                  {errors.name}
                </small>
              )}
            </label>
            {isEditMode &&
              form.slug && (
                <label className="menu-form-field">
                  <span>
                    {t(
                      'menus.form.slug',
                    )}
                  </span>
                  <input
                    type="text"
                    value={form.slug}
                    readOnly
                    disabled
                  />
                  <small className="menu-form-field__hint">
                    {t(
                      'menus.form.slugHint',
                    )}
                  </small>
                </label>
              )}
            <label
              className={`menu-form-field menu-form-field--full ${
                errors.description
                  ? 'menu-form-field--invalid'
                  : ''
              }`}
            >
              <span>
                {t(
                  'menus.form.description',
                )}
              </span>
              <textarea
                rows={5}
                value={
                  form.description
                }
                onChange={(event) =>
                  handleFieldChange(
                    'description',
                    event.target.value,
                  )
                }
                placeholder={t(
                  'menus.form.descriptionPlaceholder',
                )}
              />
              {errors.description && (
                <small>
                  {errors.description}
                </small>
              )}
            </label>
            <div
              className={`menu-form-field ${errors.default_language ? 'menu-form-field--invalid' : ''}`}
            >
              <span id="menu-default-language-label">{t('menus.form.language')} *</span>
              <LanguageDropdown
                value={form.default_language}
                label={t('menus.form.language')}
                invalid={Boolean(errors.default_language)}
                onChange={handleDefaultLanguageChange}
                options={SUPPORTED_LOCALES.map((locale) => ({
                  value: locale,
                  label: t(`languages.${locale}`),
                }))}
              />
              {errors.default_language && <small>{errors.default_language}</small>}
            </div>
            <div className="menu-form-field menu-form-field--full" style={{ minWidth: 0 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Settings2 size={17} />
                {language === 'fr' ? 'Langues disponibles dans le menu' :
                 language === 'ar' ? 'اللغات المتاحة في القائمة' :
                 language === 'es' ? 'Idiomas disponibles en el menú' :
                 language === 'de' ? 'Verfügbare Menüsprachen' :
                 'Languages available in this menu'}
                {' '}({form.enabled_languages.length}/{SUPPORTED_LOCALES.length})
              </span>
              <small className="menu-form-field__hint" style={{ display: 'block', marginBottom: 10 }}>
                {language === 'fr' ? 'Choisissez les langues proposées aux clients. La langue par défaut reste toujours activée.' :
                 language === 'ar' ? 'اختر اللغات التي تظهر للزبائن. تبقى اللغة الافتراضية مفعلة دائمًا.' :
                 language === 'es' ? 'Elige los idiomas para los clientes. El idioma predeterminado siempre estará activado.' :
                 language === 'de' ? 'Wählen Sie die Sprachen für Gäste. Die Standardsprache bleibt immer aktiviert.' :
                 'Choose which languages customers can use. The default language always stays enabled.'}
              </small>
              <div className="menu-form-check-list" style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))',
                gap: 8, maxHeight: 'none', overflow: 'visible',
              }}>
                {SUPPORTED_LOCALES.map((locale) => {
                  const mandatory = locale === form.default_language;
                  const checked = mandatory || form.enabled_languages.includes(locale);
                  return (
                    <label key={locale} className={`menu-form-check-item ${checked ? 'is-selected' : ''}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: mandatory ? 'not-allowed' : 'pointer' }}>
                      <input type="checkbox" name={`enabled_languages.${locale}`}
                        checked={checked} disabled={mandatory}
                        onChange={() => toggleMenuLanguage(locale)} />
                      <span><strong>{t(`languages.${locale}`)}</strong>
                        {mandatory && <small>({language === 'fr' ? 'par défaut' : language === 'ar' ? 'افتراضي' : 'default'})</small>}
                      </span>
                    </label>
                  );
                })}
              </div>
              {errors.enabled_languages && <small role="alert">{errors.enabled_languages}</small>}
            </div>
            <label
              className={`menu-form-field ${
                errors.menu_template_id
                  ? 'menu-form-field--invalid'
                  : ''
              }`}
            >
              <span>
                {t(
                  'menus.form.template',
                )}
              </span>
              <LanguageDropdown
                value={String(form.menu_template_id || '')}
                onChange={(value) => handleFieldChange('menu_template_id', value)}
                label={t('menus.form.template')}
                invalid={Boolean(errors.menu_template_id)}
                icon={Palette}
                options={[
                  { value: '', label: t('menus.form.noTemplate') },
                  ...templates.map((template) => ({
                    value: String(template.id),
                    label: getTemplateName(template, language),
                  })),
                ]}
              />
              {errors.menu_template_id && (
                <small>
                  {
                    errors.menu_template_id
                  }
                </small>
              )}
            </label>
            <label
              className={`menu-form-field ${
                errors.sort_order
                  ? 'menu-form-field--invalid'
                  : ''
              }`}
            >
              <span>
                {t(
                  'menus.form.sortOrder',
                )}
              </span>
              <input
                type="number"
                min="0"
                step="1"
                value={
                  form.sort_order
                }
                onChange={(event) =>
                  handleFieldChange(
                    'sort_order',
                    event.target.value,
                  )
                }
              />
              {errors.sort_order && (
                <small>
                  {
                    errors.sort_order
                  }
                </small>
              )}
            </label>
          </div>
          <div className="menu-form-divider" />
          <div className="menu-form-section-heading">
            <span>
              <ImageIcon size={19} />
            </span>
            <div>
              <h2>
                {t('menus.form.restaurantLogoTitle')}
              </h2>
              <p>
                {t('menus.form.restaurantLogoDescription')}
              </p>
            </div>
          </div>
          <div className="menu-form-logo-field">
            <input
              ref={restaurantLogoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
              onChange={handleRestaurantLogoChange}
              hidden
            />
            {restaurantLogoPreview ? (
              <div className="menu-form-logo-preview">
                <span>
                  <img
                    src={restaurantLogoPreview}
                    alt={t('menus.form.restaurantLogoAlt')}
                  />
                </span>
                <div>
                  <strong>
                    {t('menus.form.restaurantLogoCurrent')}
                  </strong>
                  <small>
                    {restaurantLogoFile
                      ? t('menus.form.restaurantLogoPending')
                      : t('menus.form.restaurantLogoHint')}
                  </small>
                </div>
                <div className="menu-form-logo-preview__actions">
                  <button
                    type="button"
                    onClick={() =>
                      restaurantLogoInputRef.current?.click()
                    }
                  >
                    <Upload size={16} />
                    {t('menus.form.changeLogo')}
                  </button>
                  {restaurantLogoFile && (
                    <button
                      type="button"
                      className="is-danger"
                      onClick={handleCancelRestaurantLogo}
                    >
                      <X size={16} />
                      {t('common.cancel')}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="menu-form-logo-upload"
                onClick={() =>
                  restaurantLogoInputRef.current?.click()
                }
              >
                <span>
                  <Upload size={22} />
                </span>
                <strong>
                  {t('menus.form.uploadRestaurantLogo')}
                </strong>
                <small>
                  {t('menus.form.uploadRestaurantLogoHint')}
                </small>
              </button>
            )}
          </div>
          <div className="menu-form-divider" />
          <div className="menu-form-section-heading">
            <span>
              <ImageIcon size={19} />
            </span>
            <div>
              <h2>
                {t(
                  'menus.form.imageTitle',
                )}
              </h2>
              <p>
                {t(
                  'menus.form.imageDescription',
                )}
              </p>
            </div>
          </div>
          <div className="menu-form-image-field">
            <input
              ref={imageInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={
                handleImageChange
              }
              hidden
            />
            {imagePreview ? (
              <div className="menu-form-image-preview">
                <img
                  src={
                    imagePreview
                  }
                  alt={
                    form.name ||
                    t(
                      'menus.form.imagePreviewAlt',
                    )
                  }
                />
                <div className="menu-form-image-preview__actions">
                  <button
                    type="button"
                    onClick={() =>
                      imageInputRef.current?.click()
                    }
                  >
                    <Upload
                      size={16}
                    />
                    {t(
                      'menus.form.changeImage',
                    )}
                  </button>
                  <button
                    type="button"
                    className="is-danger"
                    onClick={
                      handleRemoveImage
                    }
                  >
                    <X size={16} />
                    {t(
                      'common.remove',
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="menu-form-image-upload"
                onClick={() =>
                  imageInputRef.current?.click()
                }
              >
                <span>
                  <Upload size={22} />
                </span>
                <strong>
                  {t(
                    'menus.form.uploadImage',
                  )}
                </strong>
                <small>
                  {t(
                    'menus.form.uploadImageHint',
                  )}
                </small>
              </button>
            )}
            {errors.image && (
              <small className="menu-form-image-error">
                {errors.image}
              </small>
            )}
          </div>
          <div className="menu-form-divider" />
          <div className="menu-form-section-heading">
            <span>
              <Settings2 size={19} />
            </span>
            <div>
              <h2>
                {t(
                  'menus.form.statusTitle',
                )}
              </h2>
              <p>
                {t(
                  'menus.form.statusDescription',
                )}
              </p>
            </div>
          </div>
          <div className="menu-form-status-grid">
            <label
              className={`menu-form-status-card ${
                form.is_active
                  ? 'is-selected'
                  : ''
              }`}
            >
              <div>
                <strong>
                  {t(
                    'menus.form.activeLabel',
                  )}
                </strong>
                <small>
                  {t(
                    'menus.form.activeDescription',
                  )}
                </small>
              </div>
              <input
                type="checkbox"
                checked={
                  form.is_active
                }
                onChange={(event) =>
                  handleFieldChange(
                    'is_active',
                    event.target.checked,
                  )
                }
              />
              <span className="menu-form-switch">
                <span />
              </span>
            </label>
            <label
              className={`menu-form-status-card ${
                form.is_default
                  ? 'is-selected'
                  : ''
              }`}
            >
              <div>
                <strong>
                  {t(
                    'menus.form.defaultLabel',
                  )}
                </strong>
                <small>
                  {t(
                    'menus.form.defaultDescription',
                  )}
                </small>
              </div>
              <input
                type="checkbox"
                checked={
                  form.is_default
                }
                onChange={(event) =>
                  handleFieldChange(
                    'is_default',
                    event.target.checked,
                  )
                }
              />
              <span className="menu-form-switch">
                <span />
              </span>
            </label>
          </div>
          <div className="menu-form-divider" />
          <div className="menu-form-section-heading">
            <span>
              <Layers size={19} />
            </span>
            <div>
              <h2>
                {t('menus.form.contentTitle')}
              </h2>
              <p>
                {t('menus.form.contentDescription')}
              </p>
            </div>
          </div>
          <div className="menu-form-content-grid">
            <section className="menu-form-selection-panel">
              <div className="menu-form-selection-panel__header">
                <div>
                  <strong>
                    {t('menus.form.categoriesTitle')}
                  </strong>
                  <small>
                    {selectedCategoriesCount}{' '}
                    {t('menus.form.selected')}
                  </small>
                </div>
                <div className="menu-form-selection-panel__actions">
                  <button
                    type="button"
                    onClick={selectAllVisibleCategories}
                  >
                    {t('common.selectAll')}
                  </button>
                  <button
                    type="button"
                    onClick={clearSelectedCategories}
                  >
                    {t('common.clear')}
                  </button>
                </div>
              </div>
              <label className="menu-form-selection-search">
                <Search size={15} />
                <input
                  type="search"
                  value={categorySearchTerm}
                  onChange={(event) =>
                    setCategorySearchTerm(
                      event.target.value,
                    )
                  }
                  placeholder={t('menus.form.searchCategories')}
                />
              </label>
              <div className="menu-form-check-list">
                {filteredAvailableCategories.length > 0 ? (
                  filteredAvailableCategories.map((category) => {
                    const checked = selectedCategoryIdSet.has(
                      String(category.id),
                    );
                    return (
                      <label
                        key={category.id}
                        className={`menu-form-check-item ${
                          checked ? 'is-selected' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            toggleCategorySelection(
                              category.id,
                            )
                          }
                        />
                        <span>
                          <strong>
                            {getTranslationValue(
                              category,
                              'name',
                              language,
                              form.default_language,
                            ) ||
                              t('menus.form.unnamedCategory')}
                          </strong>
                          {getTranslationValue(
                            category,
                            'description',
                            language,
                            form.default_language,
                          ) && (
                            <small>
                              {getTranslationValue(
                                category,
                                'description',
                                language,
                                form.default_language,
                              )}
                            </small>
                          )}
                        </span>
                      </label>
                    );
                  })
                ) : (
                  <div className="menu-form-selection-empty">
                    <Layers size={21} />
                    <p>
                      {t('menus.form.noCategories')}
                    </p>
                  </div>
                )}
              </div>
            </section>
            <section className="menu-form-selection-panel">
              <div className="menu-form-selection-panel__header">
                <div>
                  <strong>
                    {t('menus.form.productsTitle')}
                  </strong>
                  <small>
                    {selectedProductsCount}{' '}
                    {t('menus.form.selected')}
                  </small>
                </div>
                <div className="menu-form-selection-panel__actions">
                  <button
                    type="button"
                    onClick={selectAllVisibleProducts}
                  >
                    {t('common.selectAll')}
                  </button>
                  <button
                    type="button"
                    onClick={clearSelectedProducts}
                  >
                    {t('common.clear')}
                  </button>
                </div>
              </div>
              <label className="menu-form-selection-search">
                <Search size={15} />
                <input
                  type="search"
                  value={productSearchTerm}
                  onChange={(event) =>
                    setProductSearchTerm(
                      event.target.value,
                    )
                  }
                  placeholder={t('menus.form.searchProducts')}
                />
              </label>
              <div className="menu-form-check-list">
                {filteredAvailableProducts.length > 0 ? (
                  filteredAvailableProducts.map((product) => {
                    const checked = selectedProductIdSet.has(
                      String(product.id),
                    );
                    const category =
                      flattenedAvailableCategories.find(
                        (currentCategory) =>
                          Number(currentCategory.id) ===
                          Number(product.category_id),
                      );
                    return (
                      <label
                        key={product.id}
                        className={`menu-form-check-item ${
                          checked ? 'is-selected' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            toggleProductSelection(product)
                          }
                        />
                        <span>
                          <strong>
                            {getTranslationValue(
                              product,
                              'name',
                              language,
                              form.default_language,
                            ) ||
                              t('menus.form.unnamedProduct')}
                          </strong>
                          <small>
                            {getTranslationValue(
                              category,
                              'name',
                              language,
                              form.default_language,
                            ) ||
                              t('menus.form.noCategory')}
                          </small>
                        </span>
                      </label>
                    );
                  })
                ) : (
                  <div className="menu-form-selection-empty">
                    <ListChecks size={21} />
                    <p>
                      {t('menus.form.noProducts')}
                    </p>
                  </div>
                )}
              </div>
            </section>
          </div>
          <div className="menu-form-divider" />
          <div className="menu-form-section-heading">
            <span>
              <UtensilsCrossed size={19} />
            </span>
            <div>
              <h2>
                {t('menus.form.featuredProductsTitle')}
              </h2>
              <p>
                {t('menus.form.featuredProductsDescription')}
              </p>
            </div>
          </div>
          <section className="menu-form-selection-panel">
            <div className="menu-form-selection-panel__header">
              <div>
                <strong>
                  {t('menus.form.featuredProductsTitle')}
                </strong>
                <small>
                  {featuredProductsCount}{' '}
                  {t('menus.form.selected')}
                </small>
              </div>
              <div className="menu-form-selection-panel__actions">
                <button
                  type="button"
                  onClick={selectAllVisibleFeaturedProducts}
                  disabled={selectedProductIds.length === 0}
                >
                  {t('common.selectAll')}
                </button>
                <button
                  type="button"
                  onClick={clearFeaturedProducts}
                  disabled={featuredProductIds.length === 0}
                >
                  {t('common.clear')}
                </button>
              </div>
            </div>
            {selectedProductIds.length > 0 ? (
              <div className="menu-form-check-list">
                {availableProducts
                  .filter((product) =>
                    selectedProductIdSet.has(
                      String(product.id),
                    ),
                  )
                  .map((product) => {
                    const checked =
                      featuredProductIdSet.has(
                        String(product.id),
                      );
                    const category =
                      flattenedAvailableCategories.find(
                        (currentCategory) =>
                          Number(currentCategory.id) ===
                          Number(product.category_id),
                      );
                    return (
                      <label
                        key={product.id}
                        className={`menu-form-check-item ${
                          checked ? 'is-selected' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            toggleFeaturedProduct(
                              product.id,
                            )
                          }
                        />
                        <span>
                          <strong>
                            {getTranslationValue(
                              product,
                              'name',
                              language,
                              form.default_language,
                            ) ||
                              t('menus.form.unnamedProduct')}
                          </strong>
                          <small>
                            {getTranslationValue(
                              category,
                              'name',
                              language,
                              form.default_language,
                            ) ||
                              t('menus.form.noCategory')}
                          </small>
                        </span>
                      </label>
                    );
                  })}
              </div>
            ) : (
              <div className="menu-form-selection-empty">
                <UtensilsCrossed size={21} />
                <p>
                  {t('menus.form.selectProductsFirst')}
                </p>
              </div>
            )}
          </section>
          {isEditMode && (
            <div className="menu-form-customize-callout">
              <span>
                <Palette size={21} />
              </span>
              <div>
                <strong>
                  {t(
                    'menus.form.customizeTitle',
                  )}
                </strong>
                <p>
                  {t(
                    'menus.form.customizeDescription',
                  )}
                </p>
              </div>
              <Link
                to={`/menus/${menuId}/customize`}
                className="dashboard-secondary-button"
              >
                <Palette size={17} />
                {t(
                  'menus.form.customize',
                )}
              </Link>
            </div>
          )}
        </form>
        <aside className="menu-form-preview-card">
          <div className="menu-form-preview-heading">
            <div>
              <p>
                {t(
                  'menus.form.previewEyebrow',
                )}
              </p>
              <h2>
                {t(
                  'menus.form.previewTitle',
                )}
              </h2>
              <small>
                {isEditMode
                  ? t(
                      'menus.form.previewEditHint',
                    )
                  : t(
                      'menus.form.previewCreateHint',
                    )}
              </small>
            </div>
            <div className="menu-form-preview-heading__right">
              {form.is_active ? (
                <span className="menu-form-preview-status is-active">
                  <CheckCircle2
                    size={14}
                  />
                  {t(
                    'menus.status.active',
                  )}
                </span>
              ) : (
                <span className="menu-form-preview-status is-inactive">
                  {t(
                    'menus.status.inactive',
                  )}
                </span>
              )}
              <div className="menu-form-zoom-control">
                <button
                  type="button"
                  onClick={() => {
                    setIsPreviewZoomManual(true);
                    setPreviewZoom(
                      (current) =>
                        Math.max(
                          0.28,
                          Number(
                            (
                              current -
                              0.1
                            ).toFixed(2),
                          ),
                        ),
                    );
                  }}
                  aria-label={t('menus.form.zoomOut')}
                >
                  <ZoomOut size={15} />
                </button>
                <button
                  type="button"
                  className="menu-form-zoom-value"
                  onClick={() => {
                    setIsPreviewZoomManual(false);
                    fitPreviewToCanvas();
                  }}
                  title={t('menus.form.fitToScreen')}
                >
                  {Math.round(
                    previewZoom * 100,
                  )}%
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsPreviewZoomManual(true);
                    setPreviewZoom(
                      (current) =>
                        Math.min(
                          1.25,
                          Number(
                            (
                              current +
                              0.1
                            ).toFixed(2),
                          ),
                        ),
                    );
                  }}
                  aria-label={t('menus.form.zoomIn')}
                >
                  <ZoomIn size={15} />
                </button>
              </div>
            </div>
          </div>
          {loadingPreview ? (
            <div className="menu-form-preview-loading">
              <Loader2 size={28} />
              <p>
                {t(
                  'menus.form.loadingPreview',
                )}
              </p>
            </div>
          ) : (
            <div
              ref={previewStageRef}
              className="menu-form-preview-stage"
              data-preview-hint={t('menus.form.previewWheelHint')}
              data-menu-layout={
                previewDesignSettings.layout ||
                'classic'
              }
              data-menu-theme={
                previewDesignSettings.theme ||
                'light'
              }
              data-card-shadow={
                previewDesignSettings.card_shadow ||
                'soft'
              }
              data-logo-position={
                previewDesignSettings.logo_position ||
                'top-right'
              }
              data-header-align={
                previewDesignSettings.header_alignment ||
                'left'
              }
              data-header-layout={
                previewDesignSettings.header_layout ||
                'classic'
              }
              data-card-style={
                previewDesignSettings.card_style ||
                'elevated'
              }
              data-image-ratio={
                previewDesignSettings.product_image_ratio ||
                '4:3'
              }
              data-category-style={
                previewDesignSettings.category_style ||
                'pills'
              }
              data-font-family={
                previewDesignSettings.font_family ||
                'system'
              }
              style={{
                '--custom-logo-size':
                  `${previewDesignSettings.logo_size || 72}px`,
                '--custom-content-width':
                  `${previewDesignSettings.content_width || 1100}px`,
                '--custom-section-gap':
                  `${previewDesignSettings.section_gap || 24}px`,
                '--custom-heading-scale':
                  (previewDesignSettings.heading_scale || 100) / 100,
                '--custom-body-scale':
                  (previewDesignSettings.body_scale || 100) / 100,
                '--custom-surface':
                  previewDesignSettings.surface_color ||
                  '#ffffff',
                '--custom-accent':
                  previewDesignSettings.accent_color ||
                  '#D7E3E0',
                '--preview-zoom':
                  previewZoom,
                '--preview-scaled-width':
                  `${Math.ceil(
                    PREVIEW_PHONE_WIDTH *
                      previewZoom,
                  )}px`,
                '--preview-scaled-height':
                  `${Math.ceil(
                    PREVIEW_PHONE_HEIGHT *
                      previewZoom,
                  )}px`,
              }}
            >
              <div className="menu-form-preview-zoom-frame">
                <div className="menu-form-preview-pan-surface">
                  <div className="menu-form-preview-scale-layer">
                    <div className="menu-form-public-phone">
                      <PublicMenuExperience
                        mode="preview"
                        phonePreview
                        menu={{
                          ...previewMenu,
                          design_settings:
                            previewDesignSettings,
                        }}
                        establishment={
                          previewEstablishment
                        }
                        categories={
                          previewCategories
                        }
                        products={
                          previewProducts
                        }
                        featuredProducts={
                          previewFeaturedProducts
                        }
                        template={
                          effectivePreviewTemplate
                        }
                        designSettings={
                          previewDesignSettings
                        }
                        orderingEnabled={Boolean(
                          menuPreviewData
                            ?.menu
                            ?.ordering_enabled ??
                            menuPreviewData
                              ?.establishment
                              ?.ordering_enabled ??
                            false,
                        )}
                        language={
                          form.default_language
                        }
                        searchable
                        className="menu-form-live-preview"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {!isEditMode && (
            <div className="menu-form-preview-note">
              <ImageIcon size={17} />
              <p>
                {t(
                  'menus.form.previewNoProductsHint',
                )}
              </p>
            </div>
          )}
        </aside>
      </section>
      <QrPrintStaticRecommendationsModal
        open={qrStaticRecommendationsOpen}
        menu={createdMenu}
        establishmentId={
          createdMenu?.establishment_id ||
          Number(establishmentId)
        }
        onClose={() => {
          setQrStaticRecommendationsOpen(false);
          if (createdMenu?.id) {
            navigate(
              `/menus/${createdMenu.id}/edit`,
              {
                replace: true,
              },
            );
          }
        }}
      />
    </div>
  );
}
export default MenuForm;
